import { ActionIcon, Anchor, Box, Group, Loader, Stack, Text, ThemeIcon } from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { CloudArrowUpIcon, FileTextIcon, WarningCircleIcon, XIcon } from "@phosphor-icons/react";
import React, { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { i18n } from "@dehub/theme";
import { MediaApi } from "../../apis";
import { message } from "../../utils/Message";
import classes from "./MultiFileUpload.module.css";

const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPT = [MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.webp, MIME_TYPES.heic];

type UploadItem = {
  file: File;
  isUpload: boolean;
  failed?: boolean;
  url: string | null;
};

type Props = {
  onChange?: (files: string[]) => void;
  value?: any;
  error?: any;
};

export function MultiFileUpload({ onChange, error }: Props) {
  const [files, setFiles] = React.useState<Record<string, UploadItem>>({});

  const onDrop = (blobs: File[]) => {
    const uploads = blobs.map((blob) => {
      const uid = uuidv4();
      (async () => {
        try {
          const form = new FormData();
          form.append("file", blob);
          const res = await MediaApi.uploadFile(form);
          setFiles((state) => (state[uid] ? { ...state, [uid]: { ...state[uid], isUpload: true, url: res.url } } : state));
        } catch (err) {
          setFiles((state) => (state[uid] ? { ...state, [uid]: { ...state[uid], failed: true } } : state));
        }
      })();
      return [uid, { file: blob, isUpload: false, url: null }] as const;
    });

    setFiles((state) => ({ ...state, ...Object.fromEntries(uploads) }));
  };

  const onRemove = (uid: string) => {
    setFiles((state) => {
      const next = { ...state };
      delete next[uid];
      return next;
    });
  };

  useEffect(() => {
    if (onChange) onChange(Object.values(files).filter((f) => f.url).map((f) => f.url as string));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  return (
    <Stack gap="xs">
      <Dropzone
        className={classes.dropzone}
        data-error={error ? true : undefined}
        onDrop={onDrop}
        onReject={() => message.error(i18n.t("common:uploadRejected"))}
        maxSize={MAX_SIZE}
        accept={ACCEPT}
        multiple
      >
        <Group gap="sm" wrap="nowrap" className={classes.dropzoneInner}>
          <ThemeIcon variant="light" color="brand" radius="xl" size={36}>
            <CloudArrowUpIcon size={20} />
          </ThemeIcon>
          <Box miw={0}>
            <Text fz="sm" fw={500}>
              {i18n.t("common:uploadDrop")}{" "}
              <Text span c="gray.9" fw={600}>
                {i18n.t("common:uploadBrowse")}
              </Text>
            </Text>
            <Text fz="sm" c="dimmed">
              {i18n.t("common:uploadHint")}
            </Text>
          </Box>
        </Group>
      </Dropzone>

      {Object.entries(files).map(([uid, item]) => (
        <Group key={uid} className={classes.fileRow} gap="xs" wrap="nowrap">
          {item.failed ? (
            <WarningCircleIcon size={18} color="var(--mantine-color-red-6)" />
          ) : item.isUpload ? (
            <FileTextIcon size={18} color="var(--mantine-color-brand-6)" />
          ) : (
            <Loader size={14} />
          )}
          {item.url ? (
            <Anchor href={item.url} target="_blank" fz="sm" truncate flex={1}>
              {item.file.name}
            </Anchor>
          ) : (
            <Text fz="sm" truncate flex={1} c={item.failed ? "red.8" : "dimmed"}>
              {item.file.name}
            </Text>
          )}
          <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => onRemove(uid)}>
            <XIcon size={14} />
          </ActionIcon>
        </Group>
      ))}
    </Stack>
  );
}
